import React, { FC, useEffect, useMemo, useState } from 'react';
import { useMediaPredicate } from 'react-media-hook';
import { Button, Col, Layout, Row } from 'antd';
import moment from 'moment';
import { PageContent } from 'common/components/Page/PageContent';
import { PageHeader } from 'common/components/Page/PageHeader';
import { PageHeaderMob } from 'common/components/Page/PageHeaderMob';
import { PageStoreSelector } from 'common/components/PageStoreSelector';
import { Selector } from 'common/components/Selector/Selector';
import { PeriodFilterCard } from 'common/components/PeriodFilterCard';
import { MEDIA_MOBILE_768 } from 'common/const/common.const';
import { ESidebarMenuKey, ESidebarMenuLabel, sidebarMenuIcons } from 'common/models/commonModel';
import { Calendar, Funnel } from 'common/svg';
import { getWeekOptions } from 'common/helpers/date.helper';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesStoresCollection } from 'app/store/reducers/monitorSales.reducer';
import { SalesSummaryBlock } from 'entities/MonitorSales/components/SalesSummaryBlock';
import { CategorySalesBlock } from 'entities/MonitorSales/components/CategorySalesBlock';
import { ProductSalesBlock } from 'entities/MonitorSales/components/ProductSalesBlock';
import { ProductRatioBlock } from 'entities/MonitorSales/components/ProductRatioBlock';
import { OosOpportunityLossBlock } from 'entities/MonitorSales/components/OosOpportunityLossBlock';
import { SalesHeatmapBlock } from 'entities/MonitorSales/components/SalesHeatmapBlock';
import { getMonitorSalesFilterWeek } from 'entities/MonitorSales/monitorSales.helpers';

export const MonitorSalesPage: FC = () => {
  const dispatch = useAppDispatch();

  const { monitorSalesStoresCollection } = useAppSelector((state) => state.monitorSales);

  const isMobile: boolean = useMediaPredicate(MEDIA_MOBILE_768);

  const [storeCode, setStoreCode] = useState<string | number | undefined>(undefined);
  const [openPeriodFilterCard, setOpenPeriodFilterCard] = useState<boolean>(false);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [week, setWeek] = useState<number | undefined>(undefined);
  const resolvedWeek = week ?? moment().isoWeek();
  const resolvedYear = year ?? moment().year();
  const weekStart = moment().isoWeekYear(resolvedYear).isoWeek(resolvedWeek).startOf('isoWeek');
  const weekEnd = weekStart.clone().endOf('isoWeek');
  const coverage = `${weekStart.format('MMM D, YYYY')} to ${weekEnd.format('MMM D, YYYY')} (12:00 am — 11:59 pm)`;
  const filterWeek = getMonitorSalesFilterWeek(year, week);
  const filterActive = openPeriodFilterCard || storeCode || week;
  const weekOptions = useMemo(() => getWeekOptions(year), [year]);

  useEffect(() => {
    dispatch(getMonitorSalesStoresCollection());
  }, []);

  const openFilterCard = () => {
    setOpenPeriodFilterCard(true);
  };

  const closeFilterCard = () => {
    setOpenPeriodFilterCard(false);
  };

  const changeStore = (value?: string | number) => {
    setStoreCode(value);
  };

  const changeYear = (year?: number): void => {
    setYear(year as number);
  };

  const changeWeek = (week?: number): void => {
    setWeek(week);
  };

  const handleChangeWeek = (week?: string | number): void => {
    if (!week) {
      setYear(undefined);
      setWeek(undefined);
    } else {
      setWeek(week as number);
    }
  };

  const headerFilters = (
    <Row gutter={[8, 8]} align="middle">
      <Col style={{ maxWidth: 149 }}>
        <PageStoreSelector stores={monitorSalesStoresCollection?.data} onChange={changeStore} />
      </Col>
      <Col>
        <Selector
          suffix={<Calendar />}
          placeholder={
            <>
              <Calendar />
              <span>Select Period</span>
            </>
          }
          options={weekOptions}
          value={week}
          onChange={handleChangeWeek}
        />
      </Col>
      <Col>
        <Button
          className={`btn-gray square ${filterActive ? 'btn-gray-selected' : ''}`}
          icon={<Funnel className={filterActive ? 'icon-purple' : 'icon-gray'} />}
          onClick={openFilterCard}
        />
      </Col>
    </Row>
  );

  return (
    <Layout className="relative height-full flex flex__column">
      {isMobile ? (
        <PageHeaderMob
          title={
            <div className="mb-3 flex flex-align-center">
              {sidebarMenuIcons[ESidebarMenuKey.MonitorSales]}
              <span className="ml-3 ff-regular">{ESidebarMenuLabel[ESidebarMenuKey.MonitorSales]}</span>
            </div>
          }
          extra={[<span key="filters">{headerFilters}</span>]}
        />
      ) : (
        <PageHeader
          title={
            <div>
              <span>{ESidebarMenuLabel[ESidebarMenuKey.MonitorSales]}</span>
            </div>
          }
          extra={headerFilters}
        />
      )}

      <PageContent>
        <div
          className={`page-content__container page-content__scroll-container ${
            isMobile ? 'scrollbar-none' : 'scrollbar-x'
          }`}
        >
          <Row gutter={12}>
            <Col span={16}>
              <Row className="mb-5">
                <Col span={24}>
                  <SalesSummaryBlock
                    dateRange={coverage}
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
              </Row>
              <Row gutter={12} align="stretch" className="mb-5">
                <Col span={16} xxl={18}>
                  <CategorySalesBlock
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
                <Col span={8} xxl={6}>
                  <ProductRatioBlock
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
              </Row>
              <Row gutter={[12, 12]} align="stretch">
                <Col span={24} xxl={14}>
                  <ProductSalesBlock
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
                <Col span={24} xxl={10}>
                  <OosOpportunityLossBlock
                    params={{
                      storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                      week: filterWeek,
                    }}
                  />
                </Col>
              </Row>
            </Col>

            <Col span={8}>
              <SalesHeatmapBlock
                params={{
                  storeCode: storeCode !== undefined ? String(storeCode) : undefined,
                  week: filterWeek,
                }}
              />
            </Col>
          </Row>
        </div>

        <PeriodFilterCard
          open={openPeriodFilterCard}
          year={year}
          week={week}
          onYearChange={changeYear}
          onWeekChange={changeWeek}
          onCancel={closeFilterCard}
        />
      </PageContent>
    </Layout>
  );
};
